import type { Rule } from 'antd/es/form';

/**
 * Unified password policy constants.
 * All password flows (activate, reset, change) use the same rules.
 */
export const PASSWORD_MIN_LENGTH = 10;

export const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&*()_+\-=[\]{};':"\\|,.<>/?!~`])/;

export const PASSWORD_PATTERN_MESSAGE =
  'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';

/** Human-readable list of requirements for display in the UI */
export const PASSWORD_REQUIREMENTS = [
  `At least ${PASSWORD_MIN_LENGTH} characters`,
  'Both upper and lowercase letters',
  'At least one number (0-9)',
  'At least one special character (@#$%^&*, etc.)',
];

/**
 * Ant Design Form.Item rules for a password field.
 * Use with `rules={passwordRules}` on the password Form.Item.
 */
export const passwordRules: Rule[] = [
  { required: true, message: 'Please enter a password' },
  {
    min: PASSWORD_MIN_LENGTH,
    message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
  },
  {
    pattern: PASSWORD_PATTERN,
    message: PASSWORD_PATTERN_MESSAGE,
  },
];

/**
 * Safely extract an error message from a fetch Response.
 * Handles non-JSON responses and missing fields gracefully.
 *
 * @param response - The fetch Response object (must not have had .json() called yet)
 * @param fallbackMessage - Message to return if extraction fails
 */
export async function extractApiError(
  response: Response,
  fallbackMessage: string,
): Promise<string> {
  try {
    const data = await response.json();
    return data?.error || data?.message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
}

/**
 * Safely parse JSON from a fetch Response, returning null on failure.
 */
export async function safeParseJson<T = any>(response: Response): Promise<T | null> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
